import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import TermsPage from "./pages/legal/TermsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import OnboardingPage from "./pages/OnboardingPage";
import OnboardingStep1Page from "./pages/OnboardingStep1Page";
import OnboardingStep2Page from "./pages/OnboardingStep2Page";
import OnboardingStep3Page from "./pages/OnboardingStep3Page";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import StudentDashboard from "./pages/StudentDashboard";
import StudentTasksPage from "./pages/StudentTasksPage";
import TeacherDashboard from "./pages/TeacherDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLoginPage from "./pages/AdminLoginPage";
import TeacherManagementPage from "./pages/TeacherManagementPage";
import AdminStudentManagementPage from "./pages/AdminStudentManagementPage";
import ParentManagementPage from "./pages/ParentManagementPage";
import AdminCohortsManagementPage from "./pages/AdminCohortsManagementPage";
import AdminBenchmarksPage from "./pages/AdminBenchmarksPage";
import TaskRecordingPage from "./pages/TaskRecordingPage";
import FeedbackPage from "./pages/FeedbackPage";
import TeacherReviewPage from "./pages/TeacherReviewPage";
import TeacherReviewListPage from "./pages/TeacherReviewListPage";
import ProgressPage from "./pages/ProgressPage";
import NotFound from "./pages/NotFound";
import LiveDemoPage from "./pages/LiveDemoPage";
import StudentManagementPage from "./pages/StudentManagementPage";
import CreateAssessmentPage from "./pages/CreateAssessmentPage";
import CohortManagementPage from "./pages/CohortManagementPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import { ProtectedRoute } from "./components/AuthGuard";

const App = () => (
  <TooltipProvider>
    <Toaster />
    <Sonner />
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/1" element={<OnboardingStep1Page />} />
        <Route path="/onboarding/2" element={<OnboardingStep2Page />} />
        <Route path="/onboarding/3" element={<OnboardingStep3Page />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/live-demo" element={<LiveDemoPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        
        {/* Student Routes - Protected */}
        <Route
          path="/student"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/tasks"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentTasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/task/:id"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <TaskRecordingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/feedback/:id"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <FeedbackPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/progress"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <ProgressPage role="student" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/settings"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <AccountSettingsPage role="student" />
            </ProtectedRoute>
          }
        />
        
        {/* Parent Routes - Protected */}
        <Route
          path="/parent"
          element={
            <ProtectedRoute allowedRoles={['parent']}>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/children"
          element={
            <ProtectedRoute allowedRoles={['parent']}>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/progress"
          element={
            <ProtectedRoute allowedRoles={['parent']}>
              <ProgressPage role="parent" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/settings"
          element={
            <ProtectedRoute allowedRoles={['parent']}>
              <AccountSettingsPage role="parent" />
            </ProtectedRoute>
          }
        />
        
        {/* Teacher Routes - Protected */}
        <Route
          path="/teacher"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/assessments/create"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <CreateAssessmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/review"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherReviewListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/review/:id"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherReviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/students"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <StudentManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/cohorts"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <CohortManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/progress"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <ProgressPage role="teacher" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/settings"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <AccountSettingsPage role="teacher" />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Routes - Protected */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Sub-routes */}
        <Route
          path="/admin/teachers"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <TeacherManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/students"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminStudentManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/parents"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ParentManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cohorts"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCohortsManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/benchmarks"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminBenchmarksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AccountSettingsPage role="admin" />
            </ProtectedRoute>
          }
        />
        
        {/* Fallback for unknown routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </TooltipProvider>
);

export default App;
